/**
 * Module-level cache of signed storage URLs, keyed by object path.
 *
 * The quest-files bucket is private, so inline <img>/<video> need signed URLs.
 * Without a cache, every re-render of the message list would re-sign every
 * visible attachment. Entries expire 600s inside the server-side TTL so a URL
 * handed to the browser is never already dead.
 */

export const SIGNED_URL_TTL_SECONDS = 3600;
/** 3000s — the 3600s TTL minus a 600s safety margin. */
export const CACHE_LIFETIME_MS = 3_000_000;

const cache = new Map<string, { url: string; issuedAt: number }>();
const MAX_CACHE_ENTRIES = 500;

export function getCached(path: string, now: number): string | null {
  const entry = cache.get(path);
  if (!entry) return null;
  if (now - entry.issuedAt >= CACHE_LIFETIME_MS) {
    cache.delete(path);
    return null;
  }
  return entry.url;
}

export function putCached(path: string, url: string, now: number): void {
  for (const [cachedPath, entry] of cache) {
    if (now - entry.issuedAt >= CACHE_LIFETIME_MS) cache.delete(cachedPath);
  }
  if (!cache.has(path) && cache.size >= MAX_CACHE_ENTRIES) {
    const oldestPath = cache.keys().next().value;
    if (oldestPath) cache.delete(oldestPath);
  }
  cache.delete(path);
  cache.set(path, { url, issuedAt: now });
}

/** Paths that need signing — deduplicated, order-preserving. */
export function missingPaths(paths: string[], now: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of paths) {
    if (seen.has(p)) continue;
    seen.add(p);
    if (getCached(p, now) === null) out.push(p);
  }
  return out;
}

/** Clear user-scoped signed URLs on authentication transitions and in tests. */
export function clearCache(): void {
  cache.clear();
}
